// Thin fetch wrapper. JWT injected from localStorage on every call. 401 → trigger
// a global logout so the redirect to /login happens once, not 30 times.

const TOKEN_KEY = 'samvidhx.token';

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY) || '',
  set: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

type FetchOpts = Omit<RequestInit, 'body'> & { body?: unknown };

let onUnauthorized: (() => void) | null = null;
export function registerUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function api<T = unknown>(url: string, opts: FetchOpts = {}): Promise<T> {
  const headers = new Headers(opts.headers as HeadersInit | undefined);
  const token = tokenStore.get();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let body: BodyInit | undefined;
  if (opts.body !== undefined && opts.body !== null) {
    if (typeof opts.body === 'string' || opts.body instanceof FormData) {
      body = opts.body;
    } else {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(opts.body);
    }
  }

  const res = await fetch(url, { ...opts, headers, body });

  // 401 on a request that carried a token = the session is gone → log out.
  // The login call itself (or any call without a token) is a plain auth failure,
  // so fall through and surface the server's message ("Invalid credentials").
  if (res.status === 401 && token && url !== '/api/auth/login') {
    tokenStore.clear();
    onUnauthorized?.();
    throw new ApiError(401, 'Session expired');
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown;
  try { parsed = text ? JSON.parse(text) : undefined; } catch { parsed = text; }

  if (!res.ok) {
    const msg =
      (parsed && typeof parsed === 'object' && 'error' in parsed && typeof (parsed as { error: unknown }).error === 'string')
        ? (parsed as { error: string }).error
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}
